import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  categorizedShares,
  compactCount,
  formatDelta,
  formatJoined,
  formatScore,
  scorePercent,
  SCORE_HIGH_WATERMARK,
  type About,
  type RankProfile,
  type Follower,
  type RankUser,
} from "@/lib/types";
import { Cascade, CascadeItem, CountUp } from "@/components/Reveal";
import { Delta, TierBadge } from "./States";
import { relTime } from "./PeopleList";

function Avatar({
  username,
  src,
  size = "size-8",
}: {
  username: string;
  src?: string;
  size?: string;
}) {
  const [broken, setBroken] = useState(false);
  const base = `mono flex ${size} shrink-0 items-center justify-center overflow-hidden border border-ink/25 bg-bg text-[11px] font-semibold text-ink-soft`;

  if (src && !broken) {
    return (
      <span className={base}>
        <img
          src={src}
          alt={`@${username} avatar`}
          loading="lazy"
          onError={() => setBroken(true)}
          className="size-full object-cover"
        />
      </span>
    );
  }

  return (
    <span aria-hidden="true" className={base}>
      {username.charAt(0).toUpperCase()}
    </span>
  );
}

function MetaRow({ user }: { user: RankUser }) {
  const joined = formatJoined(user.created_at);
  const items = [
    user.location ? { k: "Location", v: user.location } : null,
    user.tweets_count !== undefined
      ? { k: "Posts", v: compactCount(user.tweets_count) }
      : null,
    user.followings_count !== undefined
      ? { k: "Following", v: compactCount(user.followings_count) }
      : null,
    joined ? { k: "Joined", v: joined } : null,
  ].filter(Boolean) as { k: string; v: string }[];

  if (items.length === 0) return null;

  return (
    <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:gap-y-3">
      {items.map((it) => (
        <div key={it.k} className="min-w-0">
          <dt className="label text-[9px]">{it.k}</dt>
          <dd className="mono mt-1 truncate text-[12px] text-ink-soft">
            {it.v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Score vs watermark — filled bar with 3k tick. */
function ScoreGauge({ score }: { score: number }) {
  const pct = scorePercent(score);
  const legacy = Math.abs(score) <= 3.5;
  const markLeft = legacy
    ? undefined
    : Math.min(100, (SCORE_HIGH_WATERMARK / Math.max(SCORE_HIGH_WATERMARK, score, 1)) * 100);

  return (
    <div>
      <div className="relative mt-5 h-3 w-full border border-ink/20 bg-bg">
        <div
          className="bar-grow h-full bg-accent"
          style={{ ["--bar-w" as string]: `${pct}%` }}
        />
        {!legacy && markLeft !== undefined && markLeft < 100 ? (
          <span
            aria-hidden="true"
            className="absolute top-0 bottom-0 w-px bg-ink/40"
            style={{ left: `${markLeft}%` }}
            title={`${SCORE_HIGH_WATERMARK.toLocaleString("en-US")} watermark`}
          />
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="mono text-[11px] text-ink-muted">
          {legacy
            ? "Follower-quality score · 0–3 scale"
            : `Reputation points · ${SCORE_HIGH_WATERMARK.toLocaleString("en-US")} watermark`}
        </p>
        {!legacy ? (
          <p className="mono text-[11px] text-ink-muted">
            {Math.round(pct)}% of mark
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Two-column delta bars from /score-changes. */
function DeltaBars({
  week,
  month,
}: {
  week: number;
  month: number;
}) {
  const maxAbs = Math.max(Math.abs(week), Math.abs(month), 1);
  const bars = [
    { k: "7D", v: week },
    { k: "30D", v: month },
  ];

  return (
    <div className="mt-6 border border-line bg-bg/40 p-4">
      <div className="flex items-baseline justify-between gap-2 border-b border-line pb-2">
        <p className="label">Score momentum</p>
        <p className="mono text-[11px] text-ink-muted">Δ from API</p>
      </div>
      <div className="mt-4 space-y-4">
        {bars.map((b) => {
          const up = b.v >= 0;
          const w = Math.max(4, (Math.abs(b.v) / maxAbs) * 100);
          return (
            <div key={b.k}>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className="label">{b.k}</span>
                <Delta value={b.v} />
              </div>
              <div className="h-2 w-full border border-ink/15 bg-bg">
                <div
                  className={`bar-grow h-full ${
                    up ? "bg-accent" : "bg-ink/50"
                  }`}
                  style={{ ["--bar-w" as string]: `${w}%` }}
                  title={`${up ? "+" : "−"}${formatDelta(b.v)}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Compact top-followers table: rank · avatar · handle/meta · score bar · score. */
function TopFollowersTable({ rows }: { rows: Follower[] }) {
  const navigate = useNavigate();
  const list = rows.slice(0, 20);
  if (list.length === 0) {
    return (
      <div>
        <p className="label border-b border-line pb-2">Top followers</p>
        <p className="mono py-4 text-[12px] text-ink-muted">
          No categorized accounts found in this handle&apos;s followers yet.
        </p>
      </div>
    );
  }

  const maxScore = Math.max(
    ...list.map((r) => (typeof r.score === "number" ? r.score : 0)),
    1,
  );

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-2">
        <p className="label">Top followers</p>
        <p className="mono text-[11px] text-ink-muted">by reputation score</p>
      </div>

      <div className="mt-1 overflow-x-auto">
        <table className="w-full min-w-[320px] border-collapse text-left">
          <thead>
            <tr className="border-b border-line">
              <th className="label w-8 py-2 pr-2 font-normal">#</th>
              <th className="label py-2 pr-2 font-normal">Account</th>
              <th className="label py-2 pr-2 font-normal">Score</th>
              <th className="label py-2 pl-2 text-right font-normal">Pts</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => {
              const since = relTime(r.followerDate);
              const s = typeof r.score === "number" ? r.score : undefined;
              const w =
                s !== undefined ? Math.max(4, (s / maxScore) * 100) : 0;
              return (
                <tr
                  key={r.id}
                  role="link"
                  tabIndex={0}
                  className="group border-b border-line last:border-b-0 hover:bg-bg"
                  onClick={() =>
                    void navigate({
                      to: "/profile/$handle",
                      params: { handle: r.username },
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      void navigate({
                        to: "/profile/$handle",
                        params: { handle: r.username },
                      });
                    }
                  }}
                >
                  <td className="mono py-2.5 pr-2 align-middle text-[11px] text-ink-muted">
                    {String(i + 1).padStart(2, "0")}
                  </td>
                  <td className="max-w-[11rem] py-2.5 pr-2 align-middle sm:max-w-none">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar
                        username={r.username}
                        src={r.profile_image_url}
                      />
                      <div className="min-w-0">
                        <span className="mono block truncate text-[12px] text-ink group-hover:text-accent-deep">
                          @{r.username}
                          {r.verified ? " ✓" : ""}
                        </span>
                        <p className="truncate text-[11px] text-ink-muted">
                          {compactCount(r.followers_count)} followers
                          {since ? ` · ${since}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 pr-2 align-middle">
                    <div className="h-2 w-14 border border-ink/15 bg-bg sm:w-24 md:w-36 lg:w-44">
                      {s !== undefined ? (
                        <div
                          className="bar-grow h-full bg-accent"
                          style={{ ["--bar-w" as string]: `${w}%` }}
                        />
                      ) : null}
                    </div>
                  </td>
                  <td className="py-2.5 pl-2 text-right align-middle">
                    {s !== undefined ? (
                      <span className="num text-[12px] font-semibold text-ink">
                        {formatScore(s)}
                      </span>
                    ) : (
                      <span className="mono text-[11px] text-ink-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FollowerList({
  title,
  rows,
  emptyHint,
}: {
  title: string;
  rows: Follower[];
  emptyHint: string;
}) {
  return (
    <div>
      <p className="label border-b border-line pb-2">{title}</p>
      {rows.length === 0 ? (
        <p className="mono py-4 text-[12px] text-ink-muted">{emptyHint}</p>
      ) : (
        <ul>
          {rows.slice(0, 20).map((r) => {
            const since = relTime(r.followerDate);
            return (
              <li key={`${title}-${r.id}`} className="border-b border-line">
                <Link
                  to="/profile/$handle"
                  params={{ handle: r.username }}
                  className="group flex items-center gap-3 py-2 transition-colors hover:bg-bg"
                >
                  <Avatar username={r.username} src={r.profile_image_url} />
                  <span className="min-w-0 flex-1">
                    <span className="mono block truncate text-[12px] text-ink group-hover:text-accent-deep">
                      @{r.username}
                      {r.verified ? " ✓" : ""}
                    </span>
                    <span className="block truncate text-[11px] text-ink-muted">
                      {compactCount(r.followers_count)} followers
                      {since ? ` · follows since ${since}` : ""}
                    </span>
                  </span>
                  {r.score !== undefined ? (
                    <span
                      className="num shrink-0 border border-line px-1.5 py-0.5 text-[11px] font-semibold text-ink"
                      title="Reputation score"
                    >
                      {formatScore(r.score)}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Right column: top-following when present; otherwise bundled 7d graph + trust
 * — never a second copy of the left score-ranked table.
 */
function FollowingColumn({ profile }: { profile: RankProfile }) {
  const following = profile.topFollowing;

  if (following.length > 0) {
    return (
      <FollowerList title="Top following" rows={following} emptyHint="" />
    );
  }

  return (
    <RecentGraphRail
      user={profile.user}
      about={profile.about ?? null}
      rows={profile.newFollowers7d ?? []}
      excludeIds={new Set(profile.topFollowers.map((f) => f.id))}
    />
  );
}

/** Bucket new-follower dates into the last 7 calendar days (oldest → newest). */
function weekActivity(rows: Follower[]): number[] {
  const days = [0, 0, 0, 0, 0, 0, 0];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 6);
  const startMs = start.getTime();

  for (const r of rows) {
    if (!r.followerDate) continue;
    const t = new Date(r.followerDate).getTime();
    if (Number.isNaN(t) || t < startMs) continue;
    const idx = Math.min(6, Math.floor((t - startMs) / 86_400_000));
    if (idx >= 0) days[idx] += 1;
  }
  return days;
}

function RecentGraphRail({
  user,
  about,
  rows,
  excludeIds,
}: {
  user: RankUser;
  about: About | null;
  rows: Follower[];
  excludeIds: Set<string>;
}) {
  const filtered = rows.filter((r) => !excludeIds.has(r.id));
  const list = filtered.slice(0, 14);
  const activity = weekActivity(rows);
  const maxDay = Math.max(...activity, 1);
  const totalNew = rows.length;
  const hasInbound = rows.length > 0;

  /** Only fields not already on the left (location / joined / ✓). */
  const trustItems: { k: string; v: string }[] = [];
  if (about) {
    const loc = (user.location ?? "").trim().toLowerCase();
    const country = (about.country ?? "").trim();
    if (
      country &&
      country.toLowerCase() !== "unknown" &&
      country.toLowerCase() !== loc
    ) {
      trustItems.push({ k: "Country", v: country });
    }
    if (about.premium_start_at) {
      trustItems.push({
        k: "Premium since",
        v: new Date(about.premium_start_at).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
      });
    }
    if (about.username_changes.length > 0) {
      trustItems.push({
        k: "Handle changes",
        v: String(about.username_changes.length),
      });
    }
    if (about.source) {
      trustItems.push({ k: "Signup", v: about.source });
    }
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-2">
          <p className="label">New followers · 7d</p>
          <p className="num text-[15px] font-semibold text-ink">
            {hasInbound ? (
              <>
                <CountUp value={totalNew} duration={900} />
                <span className="mono ml-1.5 text-[11px] font-normal text-ink-muted">
                  indexed · by date
                </span>
              </>
            ) : (
              <span className="mono text-[11px] font-normal text-ink-muted">
                by follow date · not by score
              </span>
            )}
          </p>
        </div>

        {hasInbound ? (
          <div className="mt-4 border border-line bg-bg p-3">
            <div
              className="flex h-14 items-end gap-1.5"
              role="img"
              aria-label={`New followers per day over 7 days, total ${totalNew}`}
            >
              {activity.map((n, i) => {
                const h = Math.max(8, (n / maxDay) * 100);
                return (
                  <div
                    key={i}
                    className="flex h-full flex-1 flex-col justify-end"
                    style={{ ["--i" as string]: i }}
                    title={`${n} new`}
                  >
                    <div
                      className={`bar-rise w-full ${n > 0 ? "bg-accent" : "bg-ink/10"}`}
                      style={{ ["--bar-h" as string]: `${h}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-1.5 flex justify-between">
              <span className="mono text-[10px] text-ink-muted">−6d</span>
              <span className="mono text-[10px] text-ink-muted">today</span>
            </div>
          </div>
        ) : null}

        {list.length === 0 ? (
          <p className="mono py-4 text-[12px] text-ink-muted">
            No indexed follows in the last 7 days beyond the left table.
          </p>
        ) : (
          <ul className="mt-2">
            {list.map((r) => {
              const since = relTime(r.followerDate);
              return (
                <li key={r.id} className="border-b border-line">
                  <Link
                    to="/profile/$handle"
                    params={{ handle: r.username }}
                    className="group flex items-center gap-3 py-2.5 transition-colors hover:bg-bg"
                  >
                    <Avatar username={r.username} src={r.profile_image_url} />
                    <span className="min-w-0 flex-1">
                      <span className="mono block truncate text-[12px] text-ink group-hover:text-accent-deep">
                        @{r.username}
                        {r.verified ? " ✓" : ""}
                      </span>
                      <span className="block truncate text-[11px] text-ink-muted">
                        {compactCount(r.followers_count)} followers
                        {since ? ` · ${since}` : ""}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className="size-1.5 shrink-0 bg-accent"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {trustItems.length > 0 ? (
        <div className="border border-line bg-bg-panel">
          <p className="label border-b border-line px-4 py-2">Extra trust</p>
          <dl
            className={`grid ${
              trustItems.length === 1 ? "grid-cols-1" : "grid-cols-2"
            }`}
          >
            {trustItems.map((it, i) => (
              <div
                key={it.k}
                className={`px-4 py-3 ${
                  trustItems.length > 1 && i % 2 === 1
                    ? "border-l border-line"
                    : ""
                } ${i >= 2 ? "border-t border-line" : ""}`}
              >
                <dt className="label text-[9px]">{it.k}</dt>
                <dd className="mono mt-1 truncate text-[13px] font-semibold text-ink">
                  {it.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

/** Visual share only — absolute counts already live in FollowerStats. */
export function QualityBreakdown({ profile }: { profile: RankProfile }) {
  const shares = categorizedShares(profile.followersStats);
  if (!shares) return null;

  return (
    <div className="border-t border-line p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-2">
        <p className="label">Follower quality mix</p>
        <p className="mono text-[11px] text-ink-muted">
          % of {compactCount(shares.total)} categorized
        </p>
      </div>

      <div
        className="mt-4 flex h-4 w-full border border-ink/20 bg-bg"
        role="img"
        aria-label={`Influencers ${Math.round(shares.influencers)}%, projects ${Math.round(shares.projects)}%, venture capital ${Math.round(shares.vcs)}%`}
      >
        <span
          className="bar-grow h-full bg-accent"
          style={{ ["--bar-w" as string]: `${shares.influencers}%` }}
          title={`Influencers ${Math.round(shares.influencers)}%`}
        />
        <span
          className="bar-grow h-full bg-ink"
          style={{ ["--bar-w" as string]: `${shares.projects}%` }}
          title={`Projects ${Math.round(shares.projects)}%`}
        />
        <span
          className="bar-grow h-full bg-ink/35"
          style={{ ["--bar-w" as string]: `${shares.vcs}%` }}
          title={`Venture capital ${Math.round(shares.vcs)}%`}
        />
      </div>

      <p className="mono mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-muted">
        <span>
          <span className="mr-1.5 inline-block size-2 bg-accent align-middle" />
          {Math.round(shares.influencers)}% influencers
        </span>
        <span>
          <span className="mr-1.5 inline-block size-2 bg-ink align-middle" />
          {Math.round(shares.projects)}% projects
        </span>
        <span>
          <span className="mr-1.5 inline-block size-2 bg-ink/35 align-middle" />
          {Math.round(shares.vcs)}% VC
        </span>
      </p>
    </div>
  );
}

export function FollowerStats({ profile }: { profile: RankProfile }) {
  const s = profile.followersStats;
  const items = [
    { k: "Followers", v: compactCount(s.followers_count) },
    { k: "Influencers", v: compactCount(s.influencers_count) },
    { k: "Projects", v: compactCount(s.projects_count) },
    { k: "Venture capital", v: compactCount(s.venture_capitals_count) },
  ];
  return (
    <div className="grid grid-cols-2 border-t border-line md:grid-cols-4">
      {items.map((it, i) => (
        <div
          key={it.k}
          className={`p-4 sm:p-5 ${i > 0 ? "border-l border-line" : ""} ${
            i > 1 ? "border-t border-line md:border-t-0" : ""
          } ${i === 2 ? "border-l-0 md:border-l" : ""}`}
        >
          <p className="num text-[18px] font-semibold text-ink sm:text-[20px]">
            {it.v}
          </p>
          <p className="label mt-1 text-[9px]">{it.k}</p>
        </div>
      ))}
    </div>
  );
}

/** Full profile card — score, tier, deltas, stats and the two follower lists. */
export function ProfileCard({ profile }: { profile: RankProfile }) {
  const { user, score, scoreChanges } = profile;
  const scoreDecimals = Math.abs(score.score) < 10 ? 2 : 0;
  const scoreValue =
    scoreDecimals === 2 ? score.score : Math.round(score.score);

  if (!user?.username) {
    return (
      <div className="border border-ink bg-bg-panel p-5 text-[14px] text-ink-soft">
        Profile data is incomplete. Retry or check the API.
      </div>
    );
  }

  return (
    <Cascade resetKey={user.username}>
      <article className="border border-ink bg-bg-panel">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <CascadeItem
            i={0}
            className="border-b border-line p-5 sm:p-6 lg:border-r lg:border-b-0"
          >
            <div className="flex items-start gap-4">
              <Avatar
                username={user.username}
                src={user.profile_image_url}
                size="size-14"
              />
              <div className="min-w-0">
                <p className="mono truncate text-[20px] font-semibold text-ink">
                  @{user.username}
                </p>
                <p className="truncate text-[15px] text-ink-soft">
                  {user.display_name}
                  {user.verified ? " ✓" : ""}
                </p>
              </div>
            </div>
            {user.description ? (
              <p className="mt-4 max-w-[46ch] text-[14px] text-ink-soft">
                {user.description}
              </p>
            ) : null}
            {user.bio_urls?.length ? (
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                {user.bio_urls.map((u) => (
                  <li key={u}>
                    <a
                      href={u}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mono text-[12px] text-accent-deep underline underline-offset-2"
                    >
                      {u.replace(/^https?:\/\//, "")}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
            <MetaRow user={user} />
            {user.protected || profile.followersStats.user_protected ? (
              <p className="mono mt-4 border border-line px-2 py-1 text-[11px] text-ink-muted">
                Protected account — follower graph unavailable
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <TierBadge tier={profile.tier} label={profile.tierLabel} />
            </div>
          </CascadeItem>

          <CascadeItem i={1} className="p-5 sm:p-6">
            <p className="label">Hoodrank score</p>
            <p className="mt-2 font-display text-[clamp(72px,22vw,96px)] leading-[0.82] text-ink">
              <CountUp
                value={scoreValue}
                decimals={scoreDecimals}
                duration={1100}
              />
            </p>
            <ScoreGauge score={score.score} />
            <DeltaBars
              week={scoreChanges.week_delta}
              month={scoreChanges.month_delta}
            />
          </CascadeItem>
        </div>

        <CascadeItem i={2}>
          <FollowerStats profile={profile} />
        </CascadeItem>

        <CascadeItem i={3}>
          <QualityBreakdown profile={profile} />
        </CascadeItem>

        <CascadeItem
          i={4}
          className="grid grid-cols-1 gap-8 border-t border-line p-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)] sm:p-6"
        >
          <TopFollowersTable rows={profile.topFollowers} />
          <FollowingColumn profile={profile} />
        </CascadeItem>
      </article>
    </Cascade>
  );
}
